import {get} from "lodash";
import {getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated to this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'appStarter';

// Define reducer types handled by this reducers
export const REDUCER_TYPES = {
	RESET: '@appStarter/reset',
	SET_LIST_DATA: '@appStarter/set_list_data',
	CLEAR_LIST_DATA: '@appStarter/clear_list_data',
	SET_ITEM: '@appStarter/set_item',
	CLEAR_ITEM: '@appStarter/clear_item',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} appStarterListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setAppStarterListData: appStarterListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: appStarterListData
	}),
	clearAppStarterListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {AppStarterItemDataObject} appStarterItem
	 * @return {{type: string, item: AppStarterItemDataObject}}
	 */
	setAppStarterItem: appStarterItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: appStarterItem
	}),
	clearAppStarterItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {AppStarterItemDataObject}
	 */
	item: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getAppStarterList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getAppStarterListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getAppStarterListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getAppStarterListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getAppStarterItem: state => get(state, [reducerStoreKey, 'item']),
};

export default reducer;